// Chart utilities for converting Plotly.js to Chart.js and handling chart operations

// Helper function to convert Plotly.js configuration to Chart.js configuration
export const convertPlotlyToChartJs = (plotConfig) => {
    const { data, layout } = plotConfig;

    // Default Chart.js configuration
    const chartJsConfig = {
        type: 'bar', // Default type
        data: {
            labels: [],
            datasets: []
        },
        options: {
            responsive: true,
            plugins: {
                legend: {
                    position: 'top',
                },
                title: {
                    display: true,
                    text: layout?.title || 'Chart'
                }
            }
        }
    };

    if (!data || data.length === 0) {
        return chartJsConfig;
    }

    // Get chart type from the first trace
    const firstTrace = data[0];
    const chartType = mapPlotlyToChartJsType(firstTrace.type || 'bar');
    chartJsConfig.type = chartType;

    // Extract labels (x values) from the first trace if it has them
    if (firstTrace.x) {
        chartJsConfig.data.labels = firstTrace.x;
    } else if (firstTrace.labels) {
        // For pie charts
        chartJsConfig.data.labels = firstTrace.labels;
    }

    // Map each Plotly trace to a Chart.js dataset
    chartJsConfig.data.datasets = data.map((trace, index) => {
        const dataset = {
            label: trace.name || `Series ${index + 1}`,
            data: trace.y || trace.values || [],
            backgroundColor: getColorForIndex(index, trace.marker?.color),
            borderColor: getBorderColorForIndex(index, trace.line?.color),
            borderWidth: 1
        };

        // Handle specific chart types
        if (chartType === 'pie' || chartType === 'doughnut') {
            dataset.data = trace.values || [];
            dataset.backgroundColor = getColorsArray(trace.labels?.length || 0, trace.marker?.colors);
        }

        return dataset;
    });

    // Set x and y axis titles
    if (layout) {
        if (layout.xaxis?.title) {
            if (!chartJsConfig.options.scales) chartJsConfig.options.scales = {};
            if (!chartJsConfig.options.scales.x) chartJsConfig.options.scales.x = {};
            chartJsConfig.options.scales.x.title = {
                display: true,
                text: typeof layout.xaxis.title === 'string' ? layout.xaxis.title : layout.xaxis.title.text
            };
        }

        if (layout.yaxis?.title) {
            if (!chartJsConfig.options.scales) chartJsConfig.options.scales = {};
            if (!chartJsConfig.options.scales.y) chartJsConfig.options.scales.y = {};
            chartJsConfig.options.scales.y.title = {
                display: true,
                text: typeof layout.yaxis.title === 'string' ? layout.yaxis.title : layout.yaxis.title.text
            };
        }
    }

    return chartJsConfig;
};

// Map Plotly chart types to Chart.js types
const mapPlotlyToChartJsType = (plotlyType) => {
    const typeMap = {
        'bar': 'bar',
        'scatter': 'line',
        'line': 'line',
        'pie': 'pie',
        'histogram': 'bar',
        'box': 'boxplot',
        'violin': 'violin',
        'heatmap': 'heatmap',
        'contour': 'line',
        'scatterpolar': 'radar',
        'scattergeo': 'line',
        'scatter3d': 'scatter',
        'surface': 'line'
    };

    return typeMap[plotlyType] || 'bar';
};

// Generate colors for datasets
const getColorForIndex = (index, customColor) => {
    if (customColor) return customColor;

    const colors = [
        'rgba(255, 99, 132, 0.6)',
        'rgba(54, 162, 235, 0.6)',
        'rgba(75, 192, 192, 0.6)',
        'rgba(153, 102, 255, 0.6)',
        'rgba(255, 159, 64, 0.6)',
        'rgba(255, 205, 86, 0.6)',
        'rgba(201, 203, 207, 0.6)',
        'rgba(255, 99, 71, 0.6)'
    ];

    return colors[index % colors.length];
};

// Generate border colors
const getBorderColorForIndex = (index, customColor) => {
    if (customColor) return customColor;

    const colors = [
        'rgb(255, 99, 132)',
        'rgb(54, 162, 235)',
        'rgb(75, 192, 192)',
        'rgb(153, 102, 255)',
        'rgb(255, 159, 64)',
        'rgb(255, 205, 86)',
        'rgb(201, 203, 207)',
        'rgb(255, 99, 71)'
    ];

    return colors[index % colors.length];
};

// Generate array of colors for pie/doughnut charts
const getColorsArray = (count, customColors) => {
    if (customColors && Array.isArray(customColors)) return customColors;

    const defaultColors = [
        'rgba(255, 99, 132, 0.6)',
        'rgba(54, 162, 235, 0.6)',
        'rgba(75, 192, 192, 0.6)',
        'rgba(153, 102, 255, 0.6)',
        'rgba(255, 159, 64, 0.6)',
        'rgba(255, 205, 86, 0.6)',
        'rgba(201, 203, 207, 0.6)',
        'rgba(255, 99, 71, 0.6)'
    ];

    return Array(count).fill(0).map((_, i) => defaultColors[i % defaultColors.length]);
};

// Helper functions for chart display info
export const getChartTypeName = (plotConfig) => {
    if (!plotConfig.data || plotConfig.data.length === 0) return 'Unknown';

    const type = plotConfig.data[0].type || 'bar';
    return type.charAt(0).toUpperCase() + type.slice(1);
};

export const getSeriesNames = (plotConfig) => {
    if (!plotConfig.data) return 'None';

    const names = plotConfig.data.map(trace => trace.name || 'Unnamed Series');
    return names.join(', ');
};

export const getDataPointCount = (plotConfig) => {
    if (!plotConfig.data) return 0;

    let count = 0;
    plotConfig.data.forEach(trace => {
        if (trace.x) count += trace.x.length;
        else if (trace.y) count += trace.y.length;
        else if (trace.values) count += trace.values.length;
        else if (trace.z) {
            // For heatmaps and surface plots
            if (Array.isArray(trace.z)) {
                trace.z.forEach(row => {
                    if (Array.isArray(row)) count += row.length;
                });
            }
        }
    });

    return count;
};

// Extract product codes from user input
export const extractProductCodesFromText = (text) => {
    const productCodePatterns = [
        /\b([A-Z]{2,4})-(\d{1,3})([A-Z]?)\b/g,
        /\b([A-Z]{2,4})(\d{1,3})([A-Z]?)\b/g,
        /\b([A-Z]{2,4})[- ](\d{1,3})[- ]?([A-Z]?)[- ]?([A-Z0-9-]*)\b/g,
    ];

    let matches = [];

    // Apply each pattern and collect matches
    productCodePatterns.forEach(pattern => {
        const patternMatches = text.toUpperCase().match(pattern) || [];
        matches = [...matches, ...patternMatches];
    });

    // Handle specific product formats
    if (text.toUpperCase().includes("EPP") && text.toUpperCase().includes("40")) {
        matches.push("EPP-40V");
    }

    // Clean up and remove duplicates
    return [...new Set(matches.map(match => match.trim()))];
}; 